import { useState, useEffect } from 'react';
import { COIN_MAP } from '../utils/coingecko';
import { coinColor } from './red/CoinGlyph';
import { LOCAL_COIN_ICONS } from '../utils/localCoinIcons';
import { getTokenIcon } from '../utils/tokenIcons';

// Fiat-Waehrungen werden NICHT von CoinGecko geliefert (kein Image-URL).
// Wir rendern sie mit ihrem nativen Waehrungssymbol statt der 3-Buchstaben-
// Fallback-Pille — sieht im UI und PDF deutlich sauberer aus als "EUR".
const FIAT_SYMBOLS = {
  EUR: '€', USD: '$', GBP: '£', CHF: 'Fr', JPY: '¥',
  AUD: 'A$', CAD: 'C$', TRY: '₺', BRL: 'R$',
};

// Coin-Logos kommen monochrom oder transparent von CoinGecko. Damit sie in
// beiden Themes (Dark/Light) garantiert sichtbar sind, rendern wir sie immer
// auf einem dezenten hellen Kreis mit Brand-Color-Border. So sind weisse
// Logos (ONDO, ORCA, NTRN …) im Dark-Mode lesbar und schwarze Logos im
// Light-Mode kontrastieren gegen den Background.
export default function CoinImage({ sym, marketData, size = 40, src }) {
  const [imgError, setImgError] = useState(false);
  const upperSym = sym?.toUpperCase();
  const coinMeta = COIN_MAP[upperSym];
  const marketEntry = marketData?.[coinMeta?.id];
  // CoinGecko-Image bevorzugt, sonst lokales Fallback-Logo (z.B. BUSD seit
  // Paxos-Einstellung 2024 ohne `image`-Feld bei CoinGecko), sonst CDN-Icon per
  // Symbol. Das CDN-Fallback ist wichtig für Listen, in denen marketData (noch)
  // nicht für jeden Coin geladen ist — z.B. die Coin-Suche in den Alerts, wo
  // sonst nur generische 3-Buchstaben-Kreise erscheinen. Nur statische SVG-
  // Assets, kein Tracking. CSP img-src ist auf *.coingecko.com + cdn.jsdelivr.net
  // verengt (kein offenes https:). 404/CSP-Block → onError → farbiger Kreis.
  const cdnUrl = upperSym
    ? `https://cdn.jsdelivr.net/npm/cryptocurrency-icons@0.18.1/svg/color/${upperSym.toLowerCase()}.svg`
    : null;
  // Explizit übergebene URL (z.B. CoinGecko-`image` aus dem Live-Ticker) hat
  // Vorrang — sie ist zuverlässiger als das symbol-basierte CDN-Fallback, das
  // neuere Coins (SUI, ONDO …) nicht kennt. ABER: CoinGecko liefert für
  // logolose Coins einen „missing_*"-Platzhalter (die ?-Kachel) — den ignorieren
  // wir, damit stattdessen CDN-Logo bzw. der Buchstaben-Kreis greift.
  const primary = src || marketEntry?.image;
  const primaryOk = primary && !/missing/i.test(primary);
  // Reihenfolge: CoinGecko-Bild (marketData) > lokales Set (Coins ohne CoinGecko-
  // Bild, z.B. BUSD) > On-Chain-SPL-Icon (Helius, tokenIcons) > jsdelivr-CDN nach
  // Symbol > Buchstabenkreis. loadMarket lädt marketData für ALLE TX-Coins (auch
  // verkaufte) → kein per-Coin-Nachladen nötig (das brach den Haupt-Markt-Fetch).
  const rawUrl = (primaryOk ? primary : null)
    || LOCAL_COIN_ICONS[upperSym]
    || getTokenIcon(upperSym)
    || cdnUrl;

  useEffect(() => { setImgError(false); }, [rawUrl]);

  // ── Fiat-Branch: weisser Kreis mit grauem Border, Waehrungssymbol zentriert.
  //    Beruehrt sich nicht mit Coin-Branding, signalisiert "neutrale Buchungs-
  //    waehrung" — wichtig im PDF um EUR/USD von Krypto-Assets zu trennen.
  const fiatChar = FIAT_SYMBOLS[upperSym];
  if (fiatChar) {
    return (
      <div style={{
        width: size, height: size, borderRadius: '50%',
        background: '#FFFFFF',
        border: '1.5px solid #D8D6D0',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
        color: '#3A3A45',
        fontSize: size * 0.55, fontWeight: 800, letterSpacing: '-0.5px',
        lineHeight: 1,
      }}>
        {fiatChar}
      </div>
    );
  }

  const imgUrl = !imgError && rawUrl;
  const brand = coinColor(sym);
  const pad = Math.max(2, Math.round(size * 0.08));
  const inner = size - pad * 2;

  if (imgUrl) {
    return (
      <div style={{
        width: size, height: size, borderRadius: '50%',
        background: '#FFFFFF',
        border: `1px solid ${brand}33`,
        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.04)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, overflow: 'hidden',
      }}>
        <img
          src={imgUrl}
          alt={sym}
          width={inner}
          height={inner}
          onError={() => setImgError(true)}
          style={{ borderRadius: '50%', display: 'block' }}
        />
      </div>
    );
  }

  const color = coinMeta?.color || brand;
  const bg = coinMeta?.bg || '#FFFFFF';
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: bg, color, border: `1.5px solid ${color}33`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.28, fontWeight: 800, flexShrink: 0, letterSpacing: '-0.5px',
    }}>
      {sym.slice(0, 3)}
    </div>
  );
}
